import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, MapPin, Phone, User, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/apiClient";

const CATEGORIES = [
  "Fruit Seeds",
  "Vegetable Seeds",
  "Plastic Waste",
  "Paper & Cardboard",
  "Glass",
  "Metal & Cans",
  "E-Waste",
  "Textiles & Cloth",
  "Organic Compost",
];

export const DonationForm = ({ onSuccess }) => {
  const [donorName, setDonorName] = useState("");
  const [contact, setContact] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([{ category: "", quantity: "" }]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const updateItem = (idx, field, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };
  const addItem = () => setItems((prev) => [...prev, { category: "", quantity: "" }]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    const cleanItems = items.filter((it) => it.category && it.quantity.trim());
    if (!donorName.trim() || !contact.trim() || !location.trim()) {
      toast.error("Please fill in your name, contact number and pickup location.");
      return;
    }
    if (cleanItems.length === 0) {
      toast.error("Add at least one item with a category and quantity.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/donations", {
        donor_name: donorName.trim(),
        contact_number: contact.trim(),
        pickup_location: location.trim(),
        items: cleanItems,
        notes: notes.trim(),
      });
      toast.success("Thank you! Your donation request has been received.");
      setConfirmation(data.reference_number);
      setDonorName("");
      setContact("");
      setLocation("");
      setNotes("");
      setItems([{ category: "", quantity: "" }]);
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <div className="text-center py-6" data-testid="donation-confirmation">
        <div className="mx-auto h-16 w-16 rounded-full bg-forest/10 flex items-center justify-center mb-5">
          <CheckCircle2 className="h-9 w-9 text-forest" />
        </div>
        <h3 className="text-3xl font-serif text-forest-dark">Thank you!</h3>
        <p className="text-muted-foreground mt-2 leading-relaxed max-w-md mx-auto">
          Your donation request is in. Our team will contact you shortly to arrange a doorstep pickup.
        </p>
        <div className="mt-6 inline-flex flex-col items-center bg-sage/50 border border-forest/20 rounded-2xl px-8 py-4">
          <span className="text-[11px] uppercase tracking-[0.2em] text-forest-dark/70">Your reference number</span>
          <span className="text-2xl font-serif text-forest-dark tracking-wide" data-testid="confirmation-reference">
            {confirmation}
          </span>
        </div>
        <div className="mt-6">
          <Button
            type="button"
            onClick={() => setConfirmation(null)}
            data-testid="donate-again-btn"
            className="rounded-full bg-forest hover:bg-forest-dark text-white px-7 transition-transform hover:-translate-y-0.5"
          >
            Donate more items
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="donation-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-forest-dark flex items-center gap-2">
            <User className="h-3.5 w-3.5" /> Your Name
          </Label>
          <Input
            data-testid="donor-name-input"
            value={donorName}
            onChange={(e) => setDonorName(e.target.value)}
            placeholder="e.g. Prateek Shukla"
            className="rounded-xl border-stone-300 focus-visible:ring-forest bg-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-forest-dark flex items-center gap-2">
            <Phone className="h-3.5 w-3.5" /> Contact Number
          </Label>
          <Input
            data-testid="donor-contact-input"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="e.g. +91 98765 43210"
            className="rounded-xl border-stone-300 focus-visible:ring-forest bg-white"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-forest-dark flex items-center gap-2">
          <MapPin className="h-3.5 w-3.5" /> Pickup Location
        </Label>
        <Input
          data-testid="pickup-location-input"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Full address for collection"
          className="rounded-xl border-stone-300 focus-visible:ring-forest bg-white"
        />
      </div>

      <div className="space-y-3">
        <Label className="text-xs uppercase tracking-[0.2em] text-forest-dark">Items to Donate</Label>
        {items.map((item, idx) => (
          <div key={idx} className="flex flex-col sm:flex-row gap-3 sm:items-start" data-testid={`item-row-${idx}`}>
            <div className="flex-1">
              <Select value={item.category} onValueChange={(v) => updateItem(idx, "category", v)}>
                <SelectTrigger
                  data-testid={`item-category-${idx}`}
                  className="rounded-xl border-stone-300 bg-white"
                >
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`category-option-${c}`}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Input
                data-testid={`item-quantity-${idx}`}
                value={item.quantity}
                onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                placeholder="Quantity, e.g. 5 kg / 200 seeds"
                className="rounded-xl border-stone-300 focus-visible:ring-forest bg-white"
              />
            </div>
            {items.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeItem(idx)}
                data-testid={`remove-item-${idx}`}
                className="text-terracotta hover:bg-terracotta/10 rounded-xl mt-0.5"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          onClick={addItem}
          data-testid="add-item-btn"
          className="rounded-full border-forest text-forest hover:bg-forest hover:text-white transition-colors"
        >
          <Plus className="h-4 w-4 mr-1" /> Add another item
        </Button>
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-forest-dark">Notes (optional)</Label>
        <Textarea
          data-testid="donor-notes-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Best time for pickup, condition of items, etc."
          className="rounded-xl border-stone-300 focus-visible:ring-forest bg-white min-h-[90px]"
        />
      </div>

      <Button
        type="submit"
        disabled={submitting}
        data-testid="submit-donation-btn"
        className="w-full rounded-full bg-forest hover:bg-forest-dark text-white py-6 text-base transition-transform hover:-translate-y-0.5"
      >
        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Donate These Items"}
      </Button>
    </form>
  );
};

export default DonationForm;
